import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import axiosInstance from "services/AxiosInstance";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import ProgramacionFormModal from "./components/ProgramacionFormModal";
import { useProveedoresMantenimiento } from "./hooks/useProveedoresMantenimiento";

const PROGRAMACIONES_URL = "/api/v3/zonas-sociales/activos/programaciones/";
const MANTENIMIENTOS_URL = "/api/v3/zonas-sociales/activos/mantenimientos/";

const CalendarioMantenimientos = () => {
  const [programaciones, setProgramaciones] = useState([]);
  const [mantenimientos, setMantenimientos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { proveedores, createProveedor } = useProveedoresMantenimiento();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [progResponse, mantResponse] = await Promise.all([
        axiosInstance.get(PROGRAMACIONES_URL),
        axiosInstance.get(MANTENIMIENTOS_URL),
      ]);
      setProgramaciones(progResponse.data.results || progResponse.data);
      setMantenimientos(mantResponse.data.results || mantResponse.data);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const events = useMemo(() => {
    const progEvents = programaciones
      .filter(p => p.fecha_siguiente || p.fecha_inicio)
      .map((prog) => ({
        id: prog.uuid,
        title: prog.titulo,
        start: prog.fecha_siguiente || prog.fecha_inicio,
        backgroundColor: prog.tipo === "preventivo" ? "#28a745" : "#dc3545",
        borderColor: prog.tipo === "preventivo" ? "#28a745" : "#dc3545",
        extendedProps: {
          tipo: prog.tipo,
          frecuencia: prog.frecuencia,
          activo: prog.activo,
          descripcion: prog.descripcion,
          estado: prog.estado,
          isProgramacion: true,
        },
      }));

    const mantEvents = mantenimientos
      .filter(m => m.fecha_mantenimiento)
      .map((mant) => ({
        id: mant.uuid,
        title: `${mant.titulo} (${mant.estado})`,
        start: mant.fecha_mantenimiento,
        backgroundColor: mant.estado === "completado" ? "#6c757d" : (mant.tipo === "preventivo" ? "#28a745" : "#dc3545"),
        borderColor: mant.estado === "completado" ? "#6c757d" : (mant.tipo === "preventivo" ? "#28a745" : "#dc3545"),
        extendedProps: {
          tipo: mant.tipo,
          frecuencia: mant.frecuencia,
          activo: mant.activo,
          descripcion: mant.descripcion,
          estado: mant.estado,
          isProgramacion: false,
        },
      }));

    return [...progEvents, ...mantEvents];
  }, [programaciones, mantenimientos]);

  const handleEventClick = useCallback((info) => {
    const props = info.event.extendedProps;
    setSelectedEvent({
      uuid: info.event.id,
      titulo: info.event.title,
      fecha: info.event.startStr,
      tipo: props.tipo,
      frecuencia: props.frecuencia,
      activo: props.activo,
      descripcion: props.descripcion,
      estado: props.estado,
      isProgramacion: props.isProgramacion,
    });
    setShowDetailModal(true);
  }, []);

  const handleDateSelect = useCallback((selectInfo) => {
    setSelectedDate(selectInfo.startStr);
    setShowCreateModal(true);
    selectInfo.view.calendar.unselect();
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setSelectedDate(null);
  }, []);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      await axiosInstance.post(PROGRAMACIONES_URL, formData);
      handleCloseCreateModal();
      fetchData();
      setRefreshKey(k => k + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateProveedor = async (proveedorData) => {
    return await createProveedor(proveedorData);
  };

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center py-5">
          <SpinnerCitoPlus text="Cargando calendario..." />
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title">Calendario de Mantenimientos</h4>
            <div>
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Crear Programación
              </button>
              <Link to="/activos/programaciones" className="btn btn-secondary btn-sm me-2">
                <i className="bi bi-list me-2"></i>
                Ver Programaciones
              </Link>
              <Link to="/activos" className="btn btn-secondary btn-sm">
                <i className="bi bi-arrow-left me-2"></i>
                Volver a Activos
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex gap-3">
                  <div className="d-flex align-items-center">
                    <span
                      className="d-inline-block me-2"
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#28a745",
                        borderRadius: "3px",
                      }}
                    ></span>
                    <span>Preventivo</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span
                      className="d-inline-block me-2"
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#dc3545",
                        borderRadius: "3px",
                      }}
                    ></span>
                    <span>Correctivo</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span
                      className="d-inline-block me-2"
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#6c757d",
                        borderRadius: "3px",
                      }}
                    ></span>
                    <span>Completado</span>
                  </div>
                </div>
              </div>
            </div>
            <FullCalendar
              key={refreshKey}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              initialView="dayGridMonth"
              editable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              events={events}
              eventClick={handleEventClick}
              select={handleDateSelect}
              locale={esLocale}
              height="auto"
            />
          </div>
        </div>
      </div>

      <ProgramacionFormModal
        show={showCreateModal}
        onHide={handleCloseCreateModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        proveedores={proveedores}
        onCreateProveedor={handleCreateProveedor}
        initialData={selectedDate ? { fecha_inicio: selectedDate } : null}
        isEditing={false}
      />

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedEvent?.isProgramacion ? "Programación de Mantenimiento" : "Mantenimiento"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <>
              <div className="mb-3">
                <strong>Título:</strong> <p className="mb-0">{selectedEvent.titulo}</p>
              </div>
              <div className="mb-3">
                <strong>Tipo:</strong>{" "}
                <span className={`badge bg-${selectedEvent.tipo === "preventivo" ? "success" : "danger"}`}>
                  {selectedEvent.tipo === "preventivo" ? "Preventivo" : "Correctivo"}
                </span>
              </div>
              <div className="mb-3">
                <strong>Frecuencia:</strong> <p className="mb-0">{selectedEvent.frecuencia || "N/A"}</p>
              </div>
              <div className="mb-3">
                <strong>Activo:</strong> <p className="mb-0">{selectedEvent.activo?.nombre || "N/A"}</p>
              </div>
              <div className="mb-3">
                <strong>Fecha:</strong> <p className="mb-0">{selectedEvent.fecha}</p>
              </div>
              <div className="mb-3">
                <strong>Estado:</strong>{" "}
                <span className={`badge bg-${
                  selectedEvent.estado === "activo" || selectedEvent.estado === "completado" 
                    ? "success" 
                    : selectedEvent.estado === "en_proceso" 
                      ? "warning" 
                      : "secondary"
                }`}>
                  {selectedEvent.estado}
                </span>
              </div>
              {selectedEvent.descripcion && (
                <div className="mb-3">
                  <strong>Descripción:</strong>
                  <p className="mb-0">{selectedEvent.descripcion}</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CalendarioMantenimientos;
